import { listOrgs } from "@/lib/apiSdk";
import { useOrgStore } from "@/stores/org-store";
import { Loader2 } from "lucide-react";
import React, { createContext, useContext, useEffect, useState } from "react";
import { useAuth } from "./auth-context";

interface OrgContextType {
  activeOrgId: string;
  activeRole: string;
  orgs: Record<string, string>;
  loading: boolean;
  switching: boolean;
  setSwitching: (switching: boolean) => void;
  setActiveOrgId: (orgId: string) => Promise<void>;
  refreshClaims: (forceRefresh?: boolean) => Promise<void>;
}

export const OrgContext = createContext<OrgContextType | undefined>(undefined);

export const useOrg = () => {
  const context = useContext(OrgContext);
  if (!context) {
    throw new Error("useOrg must be used within an OrgProvider");
  }
  return context;
};

export const OrgProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [switching, setSwitching] = useState(false);
  const orgStore = useOrgStore();

  const refreshClaims = async (forceRefresh = false) => {
    if (!user) {
      orgStore.reset();
      setLoading(false);
      return;
    }

    try {
      setLoading(true);

      // Get active_org from the token (fast, no network hop beyond token refresh)
      const token = await user.getIdTokenResult(forceRefresh);
      const activeOrgFromClaims = (token.claims.active_org as string) || null;

      // Get the full org list from the API (not the token — avoids 1 KB claims limit)
      const orgEntries = await listOrgs();
      const orgs: Record<string, string> = {};
      for (const entry of orgEntries) {
        orgs[entry.org_id] = entry.role;
      }

      // Determine active org:
      // 1. Check localStorage first (keyed by user.uid)
      // 2. Fall back to existing store value if still valid
      // 3. Fall back to claims hint
      // 4. Fall back to first in list
      const savedOrgId = localStorage.getItem(`activeOrgId_${user.uid}`);
      let activeOrgId = "";

      if (savedOrgId && orgs[savedOrgId]) {
        activeOrgId = savedOrgId;
      } else if (orgStore.activeOrgId && orgs[orgStore.activeOrgId]) {
        activeOrgId = orgStore.activeOrgId;
      } else if (activeOrgFromClaims && orgs[activeOrgFromClaims]) {
        activeOrgId = activeOrgFromClaims;
      } else {
        activeOrgId = Object.keys(orgs)[0] ?? "";
      }

      // Persist the resolved activeOrgId in localStorage
      if (activeOrgId) {
        localStorage.setItem(`activeOrgId_${user.uid}`, activeOrgId);
      } else {
        localStorage.removeItem(`activeOrgId_${user.uid}`);
      }

      orgStore.setOrgData(orgs, activeOrgId);
    } catch (e) {
      console.error("Failed to load org data", e);
      orgStore.reset();
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refreshClaims();
  }, [user]);

  const setActiveOrgId = async (orgId: string) => {
    setSwitching(true);
    orgStore.setActiveOrgId(orgId);
    if (user) {
      if (orgId) {
        localStorage.setItem(`activeOrgId_${user.uid}`, orgId);
      } else {
        localStorage.removeItem(`activeOrgId_${user.uid}`);
      }
    }
    await new Promise((resolve) => setTimeout(resolve, 600));
    setSwitching(false);
  };

  return (
    <OrgContext.Provider
      value={{
        activeOrgId: orgStore.activeOrgId,
        activeRole: orgStore.activeRole,
        orgs: orgStore.orgs,
        loading,
        switching,
        setSwitching,
        setActiveOrgId,
        refreshClaims,
      }}
    >
      {children}
      {switching && (
        <div className="bg-background/80 animate-in fade-in fixed inset-0 z-[9999] flex flex-col items-center justify-center backdrop-blur-sm duration-200">
          <div className="bg-card flex max-w-sm flex-col items-center gap-4 rounded-xl border p-6 text-center shadow-lg">
            <Loader2 className="text-primary h-10 w-10 animate-spin" />
            <h3 className="text-lg font-semibold">Switching Organisation</h3>
            <p className="text-muted-foreground text-sm">
              Please wait while we load your workspace...
            </p>
          </div>
        </div>
      )}
    </OrgContext.Provider>
  );
};
